import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  KnowledgeSummary,
  ReviewMutationResult,
  ServiceCaseDefectStatistic,
  ServiceCaseFilters,
  ServiceCaseHierarchyNode,
  ServiceCaseObservationSummary,
  ServiceCaseScope,
} from '../../../src/modules/product-knowledge/customer-experience/admin/types'
import { ApiError, api, apiFetchFull } from './client'

export type {
  KnowledgeStatus,
  KnowledgeSummary,
  ReviewMutationResult,
  ServiceCaseDefectStatistic,
  ServiceCaseFilters,
  ServiceCaseHierarchyNode,
  ServiceCaseObservationSummary,
  ServiceCaseScope,
} from '../../../src/modules/product-knowledge/customer-experience/admin/types'

export interface ServiceCaseSelection {
  scope: ServiceCaseScope
  scopeId: string
}

const QUERY_ROOT = ['product-knowledge-service-cases'] as const

function params(values: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== '') query.set(key, String(value))
  }
  const result = query.toString()
  return result ? `?${result}` : ''
}

function scopeParams(selection: ServiceCaseSelection | null, filters: ServiceCaseFilters) {
  return {
    scope: selection?.scope,
    scope_id: selection?.scopeId,
    from: filters.from,
    to: filters.to,
    defect_code: filters.defectCode,
  }
}

export function useServiceCaseHierarchy(filters: ServiceCaseFilters) {
  return useQuery({
    queryKey: [...QUERY_ROOT, 'hierarchy', filters],
    queryFn: () => api.get<ServiceCaseHierarchyNode[]>(
      `/product-knowledge/service-cases/hierarchy/${params({
        from: filters.from,
        to: filters.to,
        defect_code: filters.defectCode,
      })}`,
    ),
  })
}

export function useServiceCaseDefects(
  selection: ServiceCaseSelection | null,
  filters: ServiceCaseFilters,
) {
  return useQuery({
    queryKey: [...QUERY_ROOT, 'defects', selection, filters],
    queryFn: () => api.get<ServiceCaseDefectStatistic[]>(
      `/product-knowledge/service-cases/defects/${params(scopeParams(selection, filters))}`,
    ),
    enabled: selection !== null,
  })
}

export function useServiceCaseObservations(
  selection: ServiceCaseSelection | null,
  defectCode: string | null,
  filters: ServiceCaseFilters,
) {
  const query = useInfiniteQuery({
    queryKey: [...QUERY_ROOT, 'observations', selection, defectCode, filters],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const response = await apiFetchFull<ServiceCaseObservationSummary[]>(
        `/product-knowledge/service-cases/observations/${params({
        ...scopeParams(selection, filters),
        defect_code: defectCode ?? undefined,
        cursor: pageParam,
        limit: 100,
        })}`,
      )
      return {
        data: response.data ?? [],
        nextCursor: (response as typeof response & { next_cursor?: string | null }).next_cursor ?? null,
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: selection !== null && defectCode !== null,
  })
  return {
    ...query,
    data: query.data?.pages.flatMap((page) => page.data),
  }
}

export function useServiceCaseKnowledge(
  selection: ServiceCaseSelection | null,
  defectCode?: string | null,
) {
  const query = useInfiniteQuery({
    queryKey: [...QUERY_ROOT, 'knowledge', selection, defectCode],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const response = await apiFetchFull<KnowledgeSummary[]>(
        `/product-knowledge/service-cases/knowledge/${params({
        scope: selection?.scope,
        scope_id: selection?.scopeId,
        defect_code: defectCode ?? undefined,
        cursor: pageParam,
        limit: 100,
        })}`,
      )
      return {
        data: response.data ?? [],
        nextCursor: (response as typeof response & { next_cursor?: string | null }).next_cursor ?? null,
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: selection !== null,
  })
  return {
    ...query,
    data: query.data?.pages.flatMap((page) => page.data),
  }
}

function useReviewMutation(
  path: (input: { knowledgeId: string }) => string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      requestId: string
      knowledgeId: string
      expectedVersion: number
      reason?: string
      safeStatement?: string
    }) => api.post<ReviewMutationResult>(path(input), {
      request_id: input.requestId,
      expected_version: input.expectedVersion,
      ...(input.reason === undefined ? {} : { reason: input.reason }),
      ...(input.safeStatement === undefined ? {} : { safe_statement: input.safeStatement }),
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_ROOT }),
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409 && !error.undecodable) {
        return queryClient.invalidateQueries({ queryKey: QUERY_ROOT })
      }
    },
  })
}

export function useApproveServiceKnowledge() {
  return useReviewMutation(({ knowledgeId }) =>
    `/product-knowledge/service-cases/knowledge/${encodeURIComponent(knowledgeId)}/approve/`)
}

export function useRejectServiceKnowledge() {
  return useReviewMutation(({ knowledgeId }) =>
    `/product-knowledge/service-cases/knowledge/${encodeURIComponent(knowledgeId)}/reject/`)
}

export function useCorrectServiceKnowledge() {
  return useReviewMutation(({ knowledgeId }) =>
    `/product-knowledge/service-cases/knowledge/${encodeURIComponent(knowledgeId)}/correct/`)
}

export function useRetireServiceKnowledge() {
  return useReviewMutation(({ knowledgeId }) =>
    `/product-knowledge/service-cases/knowledge/${encodeURIComponent(knowledgeId)}/retire/`)
}

export function usePromoteServiceKnowledge() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      requestId: string
      groupId: string
      defectCode: string
      safeStatement: string
      observationIds: string[]
    }) => api.post<ReviewMutationResult>('/product-knowledge/service-cases/knowledge/promote/', {
      request_id: input.requestId,
      group_id: input.groupId,
      defect_code: input.defectCode,
      safe_statement: input.safeStatement,
      observation_ids: input.observationIds,
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_ROOT }),
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409 && !error.undecodable) {
        return queryClient.invalidateQueries({ queryKey: QUERY_ROOT })
      }
    },
  })
}
